import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trackEvent, EVENT_TYPE_CTA, EVENT_TYPE_PAGE } from '../../utils/trackEvent.jsx';
import { COMMERCIAL_BANKING, COMMERCIAL_FINANCING, NONPROFIT_FINANCING } from '../../AllLendersPage.jsx';
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardContent, CardFooter } from "@/components/ui/card";
import { motion } from "framer-motion";
import { getApiUrl } from '../../config/api.config.jsx';

// API integration with feature flag for products
const useApiProducts = () => {
  const [apiProducts, setApiProducts] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check for debug query parameter
    const urlParams = new URLSearchParams(window.location.search);
    const useApi = urlParams.get('debug') === 'api';
    
    if (useApi) {
      setIsLoading(true);
      const apiUrl = `${getApiUrl()}/products`;
      
      fetch(apiUrl)
        .then(response => {
          return response.json();
        })
        .then(data => {
          if (data.status === 'success') {
            setApiProducts(data.data);
          } else {
            setError('Failed to load products from API');
          }
        })
        .catch(err => {
          setError('Error loading products from API');
          console.error('API Error:', err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, []);

  return { apiProducts, isLoading, error };
};

// API integration for service providers
const useServiceProviders = () => {
  const [serviceProviders, setServiceProviders] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setIsLoading(true);
    const apiUrl = `${getApiUrl()}/service-providers`;
    
    fetch(apiUrl)
      .then(response => {
        return response.json();
      })
      .then(data => {
        if (data.status === 'success') {
          setServiceProviders(data.data.serviceProviders);
        } else {
          setError('Failed to load service providers from API');
        }
      })
      .catch(err => {
        setError('Error loading service providers from API');
        console.error('Service Providers API Error:', err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  return { serviceProviders, isLoading, error };
};

const CATEGORIES = [
  { 
    id: 'commercial-financing', 
    name: 'Commercial Financing', 
    data: COMMERCIAL_FINANCING,
    description: 'Growth capital and riba-free financing solutions for your business'
  },
  { 
    id: 'commercial-banking', 
    name: 'Commercial Banking', 
    data: COMMERCIAL_BANKING,
    description: 'Day-to-day banking solutions for your business operations'
  },
  { 
    id: 'nonprofit-financing', 
    name: 'Nonprofit Financing', 
    data: NONPROFIT_FINANCING,
    description: 'Specialized riba-free financing solutions for nonprofit organizations'
  }
];

const StepResults = ({ formData, isSubmitting, error, onRetry }) => {
  const navigate = useNavigate();
  const [expandedLender, setExpandedLender] = useState(null);
  const [expandedProvider, setExpandedProvider] = useState(null);
  const [activeCategory, setActiveCategory] = useState(CATEGORIES[0].id);
  const [showResources, setShowResources] = useState(false);
  const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
  const { apiProducts, isLoading: apiLoading, error: apiError } = useApiProducts();
  const { serviceProviders, isLoading: providersLoading, error: providersError } = useServiceProviders();

  useEffect(() => {
    trackEvent(EVENT_TYPE_PAGE);
  }, [isLoggedIn, navigate]);

  const toggleDetails = (index) => {
    setExpandedLender(expandedLender === index ? null : index);
  };

  const toggleProviderDetails = (index) => {
    setExpandedProvider(expandedProvider === index ? null : index);
  };

  // Transform API data to match expected format
  const transformApiData = (products) => {
    if (!products) return [];
    
    // Filter out inactive products and lenders
    const activeProducts = products.filter(product => 
      product.isActive && 
      product.lender.isActive && 
      product.category.isActive
    );
    
    return activeProducts.map(product => ({
      id: product.id,
      name: product.lender.name,
      product: product.name,
      description: product.shortDescription,
      details: product.longDescription,
      applyUrl: product.applyUrl,
      categorySlug: product.category.slug // Add category for filtering
    }));
  };

  // Use API data if available, otherwise fall back to hardcoded data
  const getActiveLenders = () => {
    if (apiProducts) {
      const transformedProducts = transformApiData(apiProducts);
      
      // Map category IDs to API category slugs for filtering
      const categoryIdToSlug = {
        'home-loans': 'home-loans',
        'commercial-financing': 'commercial-financing', 
        'commercial-banking': 'commercial-banking',
        'nonprofit-financing': 'nonprofit-financing'
      };
      
      // Filter by active category
      const categorySlug = categoryIdToSlug[activeCategory];
      if (categorySlug) {
        return transformedProducts.filter(product => product.categorySlug === categorySlug);
      }
      return transformedProducts;
    }
    return CATEGORIES.find(cat => cat.id === activeCategory)?.data || [];
  };

  const activeLenders = getActiveLenders();
  const activeCategoryObj = CATEGORIES.find(cat => cat.id === activeCategory) || CATEGORIES[0];

  // Log empty state for analytics
  useEffect(() => {
    if (activeLenders.length === 0 && !apiLoading) {
      console.log(`Empty state shown for category: ${activeCategory}`, {
        category: activeCategory,
        apiProducts: apiProducts ? apiProducts.length : 0,
        isApiMode: new URLSearchParams(window.location.search).get('debug') === 'api'
      });
    }
  }, [activeLenders.length, activeCategory, apiLoading, apiProducts]);

  if (isSubmitting) {
    return (
      <div className="text-center py-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-4"
        >
          <div className="w-12 h-12 border-4 border-[#1a4a1a] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <h3 className="text-lg font-semibold text-gray-900">Submitting your halal loan matching form...</h3>
          <p className="text-gray-600">Please wait while we process your information with our trusted partners.</p>
        </motion.div>
      </div>
    );
  }

  if (apiLoading || providersLoading) {
    return (
      <div className="text-center py-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-4"
        >
          <div className="w-12 h-12 border-4 border-[#1a4a1a] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <h3 className="text-lg font-semibold text-gray-900">Loading Islamic financing products and resources...</h3>
          <p className="text-gray-600">Please wait while we fetch the latest riba-free options and business services.</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-4"
        >
          <div className="w-12 h-12 mx-auto text-red-500">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Submission Error</h3>
          <p className="text-red-600">{error}</p>
          <Button
            onClick={onRetry}
            className="mt-4 bg-[#1a4a1a] text-white hover:bg-[#1a4a1a]/90"
          >
            Try Again
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F7FF] -mx-3 md:-mx-4 px-3 md:px-4 -mt-3 md:-mt-4 pt-3 md:pt-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header Section */}
        <div className="bg-white rounded-lg p-4 md:p-6 shadow-sm">
          <div className="text-center mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              We have {activeLenders.length} trusted riba-free products that look like a good match for you.
            </h1>
            <div className="w-16 h-1 bg-[#1a4a1a] mx-auto mb-6"></div>
            
            <div className="max-w-2xl mx-auto">
              <h3 className="text-xl font-semibold mb-4 text-left">What's next?</h3>
              <div className="flex flex-col md:flex-row gap-4 mb-4">
                <div className="flex-1 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#1a4a1a]/10 text-[#1a4a1a] flex items-center justify-center font-semibold shrink-0">1</div>
                  <p className="font-medium text-gray-900">Review Matches</p>
                </div>
                <div className="flex-1 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#1a4a1a]/10 text-[#1a4a1a] flex items-center justify-center font-semibold shrink-0">2</div>
                  <p className="font-medium text-gray-900">Select Products</p>
                </div>
                <div className="flex-1 flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#1a4a1a]/10 text-[#1a4a1a] flex items-center justify-center font-semibold shrink-0">3</div>
                  <p className="font-medium text-gray-900">Connect</p>
                </div>
              </div>
              <p className="italic text-sm text-gray-500 text-center">You'll receive an email confirmation after selecting your trusted riba-free financial partner</p>
            </div>
          </div>
        </div>

        {/* Primary Loan Section */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">

          {/* Loan Category Tabs */}
          <div className="flex border-b">
            {CATEGORIES.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex-1 px-4 py-3 text-sm md:text-base font-medium transition-colors
                  ${activeCategory === category.id 
                    ? 'bg-[#1a4a1a] text-white' 
                    : 'text-gray-600 hover:text-[#1a4a1a] hover:bg-gray-50'}`}
              >
                {category.name}
              </button>
            ))}
          </div>

          {/* Category Description */}
          <div className="p-4 bg-gray-50 border-b">
            <p className="text-sm md:text-base text-gray-600">
              {activeCategoryObj.description}
            </p>
          </div>

          {/* Lender Cards */}
          <div className="p-4">
            {activeLenders.length > 0 ? (
              <div className="space-y-4">
                {activeLenders.map((lender, index) => (
                  <Card key={lender.id} className="overflow-hidden border-0 shadow-sm hover:shadow-md transition-shadow">
                    <CardHeader className="py-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <h2 className="text-lg md:text-xl font-semibold text-gray-900">{lender.name}</h2>
                          <h3 className="text-sm md:text-base text-gray-600">{lender.product}</h3>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleDetails(index)}
                          className="text-xs border-[#1a4a1a] text-[#1a4a1a] hover:bg-[#1a4a1a]/5"
                        >
                          {expandedLender === index ? "Hide Details" : "View Details"}
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3 py-3">
                      <p className="text-sm md:text-base text-gray-600">{lender.description}</p>
                      {expandedLender === index && (
                        <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-600">{lender.details}</p>
                        </div>
                      )}
                    </CardContent>
                    <CardFooter className="py-3">
                      <Button
                        className="w-full bg-[#1a4a1a] hover:bg-[#1a4a1a]/90 text-white py-3 text-base font-semibold rounded-lg shadow-md hover:shadow-lg transition-all"
                        onClick={(e) => {
                          e.preventDefault();
                          trackEvent(EVENT_TYPE_CTA, lender.id);
                          window.open(lender.applyUrl, '_blank');
                        }}
                      >
                        Apply Now
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 text-[#1a4a1a]/60">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">More Islamic Financing Partners Coming Soon</h3>
                <p className="text-gray-600 mb-4">We are expanding our network of riba-free financial partners.</p>
                <p className="text-sm text-gray-500">New halal financing options will be available soon.</p>
              </div>
            )}
          </div>
        </div>

        {/* Business Growth Resources Section */}
        {serviceProviders && serviceProviders.length > 0 && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="p-4 bg-gray-100 border-b">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Business Growth Resources ({serviceProviders.length})</h2>
                <p className="text-sm text-gray-600 mt-1">Professional services to help your business grow and succeed</p>
              </div>
            </div>

            <div className="p-4">
              <div className="space-y-4">
                {serviceProviders.map((provider, index) => (
                  <Card key={provider.id} className="overflow-hidden border-0 shadow-sm hover:shadow-md transition-shadow">
                    <CardHeader className="py-3">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center gap-3">
                          {provider.logo_url && (
                            <img 
                              src={provider.logo_url} 
                              alt={`${provider.name} logo`}
                              className="w-12 h-12 object-contain rounded"
                            />
                          )}
                          <div>
                            <h2 className="text-lg md:text-xl font-semibold text-gray-900">{provider.name}</h2>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {provider.types.map((type, typeIndex) => (
                                <span 
                                  key={typeIndex}
                                  className="px-2 py-1 text-xs bg-[#1a4a1a]/10 text-[#1a4a1a] rounded-full"
                                >
                                  {type}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleProviderDetails(index)}
                          className="text-xs border-[#1a4a1a] text-[#1a4a1a] hover:bg-[#1a4a1a]/5"
                        >
                          {expandedProvider === index ? "Hide Details" : "View Details"}
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3 py-3">
                      <p className="text-sm md:text-base text-gray-600">{provider.description}</p>
                      {expandedProvider === index && (
                        <div className="mt-2 p-3 bg-gray-50 rounded-lg space-y-3">
                          {provider.services && provider.services.length > 0 && (
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-2">Services Offered:</h4>
                              {provider.services.map((service, serviceIndex) => (
                                <div key={serviceIndex} className="mb-3 p-2 bg-white rounded border">
                                  <h5 className="font-medium text-gray-900">{service.name}</h5>
                                  <p className="text-sm text-gray-600 mt-1">{service.description}</p>
                                  {service.pricing && (
                                    <p className="text-sm text-[#1a4a1a] mt-1">
                                      Starting at ${service.pricing.startingAt} {service.pricing.period}
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          {provider.contactInfo && (
                            <div>
                              <h4 className="font-semibold text-gray-900 mb-2">Contact Information:</h4>
                              <div className="text-sm text-gray-600 space-y-1">
                                {provider.contactInfo.email && (
                                  <p>Email: {provider.contactInfo.email}</p>
                                )}
                                {provider.contactInfo.phone && (
                                  <p>Phone: {provider.contactInfo.phone}</p>
                                )}
                                {provider.contactInfo.website && (
                                  <p>Website: <a href={provider.contactInfo.website} target="_blank" rel="noopener noreferrer" className="text-[#1a4a1a] hover:underline">{provider.contactInfo.website}</a></p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                    <CardFooter className="py-3">
                      <Button
                        className="w-full bg-[#1a4a1a] hover:bg-[#1a4a1a]/90 text-white py-3 text-base font-semibold rounded-lg shadow-md hover:shadow-lg transition-all"
                        onClick={(e) => {
                          e.preventDefault();
                          trackEvent(EVENT_TYPE_CTA, `provider-${provider.id}`);
                          if (provider.contactInfo?.website) {
                            window.open(provider.contactInfo.website, '_blank');
                          }
                        }}
                      >
                        Connect with {provider.name}
                      </Button>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StepResults;

